/**
 * Data transformation utilities for tenant onboarding
 */
import type {
  CreateTenantRequest,
  CustomerAdmin,
  TenantType,
  TenantStatus,
} from "~/api/tenants/types";
import { formatDateToDDMMYYYY } from "./dateUtils";

/**
 * Form data types for Step 1
 */
export interface TenantInfoFormData {
  tenant_name: string;
  type: "CUSTOMER" | "LSP";
  demo_tenant: boolean;
  account_name: string;
  domains: string[];
  total_number_of_users: number | string | null;
  salesforce_id: string;
  country: string;
  status: "ACTIVE" | "INACTIVE";
  billing_start_date: Date | null;
  billing_end_date: Date | null;
  supports_payroll_plus: boolean | null;
  payslip_portal_link: string;
}

/**
 * Form data types for Step 2
 */
export interface CustomerAdminFormData {
  given_name: string;
  surname: string;
  other_name: string;
  preferred_name: string;
  contact_email: string;
  country: string;
  phone_code: string;
  phone_number: string;
}

/**
 * Convert form data to API request format
 * Returns CreateTenantRequest with DD/MM/YYYY date strings
 */
export function formatTenantRequest(
  step1Data: TenantInfoFormData,
  step2Data: CustomerAdminFormData,
  organizationId: string,
  accountManagerId?: string,
): CreateTenantRequest {
  // Format customer admin
  const customer_admin: CustomerAdmin = {
    given_name: step2Data.given_name.trim(),
    contact_email: step2Data.contact_email.trim().toLowerCase(),
    country: step2Data.country.toUpperCase(),
    ...(step2Data.surname?.trim() && { surname: step2Data.surname.trim() }),
    ...(step2Data.other_name?.trim() && {
      other_name: step2Data.other_name.trim(),
    }),
    ...(step2Data.preferred_name?.trim() && {
      preferred_name: step2Data.preferred_name.trim(),
    }),
    ...(step2Data.phone_code?.trim() && {
      phone_code: step2Data.phone_code.trim(),
    }),
    ...(step2Data.phone_number?.trim() && {
      phone_number: step2Data.phone_number.trim(),
    }),
  };

  // Convert dates to DD/MM/YYYY format strings
  if (!step1Data.billing_start_date) {
    throw new Error("Billing start date is required");
  }

  const billing_start_date = formatDateToDDMMYYYY(step1Data.billing_start_date);
  const billing_end_date = step1Data.billing_end_date
    ? formatDateToDDMMYYYY(step1Data.billing_end_date)
    : null;

  // Convert total_number_of_users to number
  if (
    step1Data.total_number_of_users === null ||
    step1Data.total_number_of_users === undefined ||
    step1Data.total_number_of_users === ""
  ) {
    throw new Error("Total number of users is required");
  }

  const total_number_of_users =
    typeof step1Data.total_number_of_users === "string"
      ? parseInt(step1Data.total_number_of_users, 10)
      : step1Data.total_number_of_users;

  if (isNaN(total_number_of_users) || total_number_of_users < 1) {
    throw new Error("Total number of users must be a valid number >= 1");
  }

  const request: CreateTenantRequest = {
    tenant_name: step1Data.tenant_name.trim(),
    type: step1Data.type as TenantType,
    demo_tenant: step1Data.demo_tenant,
    account_name: step1Data.account_name.trim(),
    domains: step1Data.domains.map((d) => d.trim()),
    total_number_of_users,
    salesforce_id: step1Data.salesforce_id.trim(),
    country: step1Data.country.toUpperCase(),
    status: step1Data.status as TenantStatus,
    billing_start_date,
    billing_end_date,
    organization_id: organizationId,
    customer_admin,
  };

  // Add Local Team specific fields if type is LSP
  if (step1Data.type === "LSP") {
    // supports_payroll_plus is required for LSP (validated in step1Validation)
    // It should always be a boolean (true or false) at this point, never null
    // Always include it when type is LSP since validation ensures it's set
    if (step1Data.supports_payroll_plus !== null) {
      request.supports_payroll_plus = step1Data.supports_payroll_plus;
    }

    // payslip_portal_link is only sent when supports_payroll_plus is true and link is provided
    const trimmedLink = step1Data.payslip_portal_link?.trim();
    if (
      step1Data.supports_payroll_plus === true &&
      trimmedLink &&
      trimmedLink.length > 0
    ) {
      request.payslip_portal_link = trimmedLink;
    }
  }

  // Add account manager ID if provided
  if (accountManagerId) {
    request.account_manager_id = accountManagerId;
  }

  return request;
}
